import React, { useEffect, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper, Typography, Button, IconButton, Table, TableHead, TableBody, TableRow,
  TableCell, TextField, FormControl, InputLabel, Select, MenuItem, Avatar, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Menu, Tooltip
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import SyncIcon from "@material-ui/icons/Sync";
import AddIcon from "@material-ui/icons/GroupAdd";
import EditIcon from "@material-ui/icons/Edit";
import PeopleIcon from "@material-ui/icons/People";
import LinkIcon from "@material-ui/icons/Link";
import SettingsIcon from "@material-ui/icons/Settings";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import SendIcon from "@material-ui/icons/Send";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import useWhatsApps from "../../hooks/useWhatsApps";
import GroupMessageDialog from "./GroupMessageDialog";

const useStyles = makeStyles((theme) => ({
  root: { padding: 16 },
  bar: { display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 16 },
  spacer: { flex: 1 },
  paper: { padding: 12, overflowX: "auto" },
  warn: { background: "rgba(255,167,38,.12)", border: "1px solid rgba(255,167,38,.5)", borderRadius: 8, padding: 10, fontSize: 13, marginBottom: 12 }
}));

const Groups = () => {
  const classes = useStyles();
  const { whatsApps } = useWhatsApps();
  const connected = (whatsApps || []).filter(w => w.status === "CONNECTED");
  const [whatsappId, setWhatsappId] = useState("");
  const [groups, setGroups] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [msgOpen, setMsgOpen] = useState(false);

  // form dialog
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [fSubject, setFSubject] = useState("");
  const [fDesc, setFDesc] = useState("");
  const [fParticipants, setFParticipants] = useState([]);
  const [fFile, setFFile] = useState(null);
  const fFileRef = useRef(null);
  const [saving, setSaving] = useState(false);

  // participants dialog
  const [partOpen, setPartOpen] = useState(false);
  const [partGroup, setPartGroup] = useState(null);
  const [partList, setPartList] = useState([]);
  const [partNew, setPartNew] = useState("");

  // settings menu
  const [anchor, setAnchor] = useState(null);
  const [menuGroup, setMenuGroup] = useState(null);

  useEffect(() => {
    if (!whatsappId && connected.length) setWhatsappId(connected[0].id);
    // eslint-disable-next-line
  }, [whatsApps]);

  useEffect(() => { if (whatsappId) loadGroups(); /* eslint-disable-next-line */ }, [whatsappId]);
  useEffect(() => { loadContacts(); }, []);

  const loadContacts = async () => {
    try {
      const { data } = await api.get("/contacts/list");
      const arr = Array.isArray(data) ? data : (data?.contacts || []);
      setContacts(arr.filter(c => !c.isGroup).map(c => ({ number: c.number, name: c.name })));
    } catch (e) { /* silencioso */ }
  };

  const loadGroups = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/groups", { params: { whatsappId, search } });
      setGroups(data || []);
    } catch (e) { toastError(e); } finally { setLoading(false); }
  };

  const sync = async () => {
    if (!whatsappId) return;
    setLoading(true);
    try {
      const { data } = await api.post("/groups/sync", { whatsappId });
      setGroups(data || []);
      toast.success(`Sincronizado: ${data?.length || 0} grupos.`);
    } catch (e) { toastError(e); } finally { setLoading(false); }
  };

  const openCreate = () => { setEditing(null); setFSubject(""); setFDesc(""); setFParticipants([]); setFFile(null); setFormOpen(true); };
  const openEdit = (g) => { setEditing(g); setFSubject(g.subject || ""); setFDesc(g.description || ""); setFFile(null); setFormOpen(true); };

  const saveForm = async () => {
    if (!fSubject.trim()) { toast.error("Informe o nome do grupo."); return; }
    setSaving(true);
    try {
      if (!editing) {
        const fd = new FormData();
        fd.append("whatsappId", whatsappId);
        fd.append("subject", fSubject);
        fd.append("description", fDesc || "");
        fd.append("participants", JSON.stringify(fParticipants.map(p => p.number)));
        if (fFile) { fd.append("file", fFile); fd.append("typeArch", "groups"); }
        await api.post("/groups", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Grupo criado.");
      } else {
        if (fSubject !== editing.subject) await api.put("/groups/subject", { whatsappId, jid: editing.jid, subject: fSubject });
        if (fDesc !== editing.description) await api.put("/groups/description", { whatsappId, jid: editing.jid, description: fDesc });
        if (fFile) {
          const fd = new FormData();
          fd.append("whatsappId", whatsappId); fd.append("jid", editing.jid);
          fd.append("file", fFile); fd.append("typeArch", "groups");
          await api.post("/groups/picture", fd, { headers: { "Content-Type": "multipart/form-data" } });
        }
        toast.success("Grupo atualizado.");
      }
      setFormOpen(false);
      loadGroups();
    } catch (e) { toastError(e); } finally { setSaving(false); }
  };

  const openParticipants = async (g) => {
    setPartGroup(g); setPartOpen(true); setPartList([]); setPartNew("");
    try {
      const { data } = await api.post("/groups/participants/list", { whatsappId, jid: g.jid });
      setPartList(data.participants || []);
    } catch (e) { toastError(e); }
  };
  const partAction = async (numbers, action) => {
    try {
      await api.post("/groups/participants", { whatsappId, jid: partGroup.jid, numbers, action });
      toast.success("Feito.");
      openParticipants(partGroup);
    } catch (e) { toastError(e); }
  };

  const doInvite = async (g) => {
    try {
      const { data } = await api.get("/groups/invite", { params: { whatsappId, jid: g.jid } });
      window.prompt("Link de convite (copie):", data.link);
    } catch (e) { toastError(e); }
  };
  const doRevoke = async (g) => {
    try { const { data } = await api.post("/groups/invite/revoke", { whatsappId, jid: g.jid }); window.prompt("Novo link (o anterior foi revogado):", data.link); }
    catch (e) { toastError(e); }
  };
  const doSetting = async (setting) => {
    try { await api.post("/groups/setting", { whatsappId, jid: menuGroup.jid, setting }); toast.success("Configuração aplicada."); }
    catch (e) { toastError(e); } finally { setAnchor(null); }
  };
  const doLeave = async (g) => {
    if (!window.confirm(`Sair do grupo "${g.subject}"?`)) return;
    try { await api.post("/groups/leave", { whatsappId, jid: g.jid }); toast.success("Saiu do grupo."); loadGroups(); }
    catch (e) { toastError(e); }
  };

  return (
    <div className={classes.root}>
      <div className={classes.bar}>
        <Typography variant="h5" style={{ fontWeight: 700 }}>Grupos</Typography>
        <FormControl style={{ minWidth: 200 }}>
          <InputLabel>Conexão</InputLabel>
          <Select value={whatsappId} onChange={(e) => setWhatsappId(e.target.value)}>
            {connected.map(w => <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField label="Buscar" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadGroups()} />
        <div className={classes.spacer} />
        <Button startIcon={<SyncIcon />} onClick={sync} disabled={loading || !whatsappId}>Sincronizar</Button>
        <Button startIcon={<AddIcon />} variant="outlined" onClick={openCreate} disabled={!whatsappId}>Criar grupo</Button>
        <Button startIcon={<SendIcon />} variant="contained" color="primary" onClick={() => setMsgOpen(true)} disabled={!whatsappId || !groups.length}>Mensagens</Button>
      </div>

      {connected.length === 0 && <div className={classes.warn}>Nenhuma conexão CONNECTED. Conecte um número para gerenciar grupos.</div>}

      <Paper className={classes.paper}>
        {loading ? <div style={{ padding: 24, textAlign: "center" }}><CircularProgress /></div> : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Grupo</TableCell>
                <TableCell align="center">Membros</TableCell>
                <TableCell align="center">Admin?</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map((g) => (
                <TableRow key={g.jid}>
                  <TableCell>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <Avatar src={g.pictureUrl || undefined}>{(g.subject || "?")[0]}</Avatar>
                      <span>{g.subject || g.jid}</span>
                    </div>
                  </TableCell>
                  <TableCell align="center">{g.participantsCount}</TableCell>
                  <TableCell align="center">{g.isBotAdmin ? <Chip size="small" label="Admin" color="primary" /> : <Chip size="small" label="Não" />}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Editar"><IconButton size="small" onClick={() => openEdit(g)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Participantes"><IconButton size="small" onClick={() => openParticipants(g)}><PeopleIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Convite"><IconButton size="small" onClick={() => doInvite(g)}><LinkIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Configurações"><IconButton size="small" onClick={(e) => { setMenuGroup(g); setAnchor(e.currentTarget); }}><SettingsIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Sair"><IconButton size="small" onClick={() => doLeave(g)}><ExitToAppIcon fontSize="small" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {groups.length === 0 && <TableRow><TableCell colSpan={4} align="center" style={{ color: "#888", padding: 24 }}>Nenhum grupo. Clique em Sincronizar.</TableCell></TableRow>}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        <MenuItem onClick={() => doSetting("onlyAdminsSend")}>Só admins enviam</MenuItem>
        <MenuItem onClick={() => doSetting("everyoneSends")}>Todos enviam</MenuItem>
        <MenuItem onClick={() => doSetting("onlyAdminsEdit")}>Só admins editam infos</MenuItem>
        <MenuItem onClick={() => doSetting("everyoneEdits")}>Todos editam infos</MenuItem>
        <MenuItem onClick={() => { setAnchor(null); doRevoke(menuGroup); }}>Revogar link de convite</MenuItem>
      </Menu>

      {/* Criar/Editar */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? "Editar grupo" : "Criar grupo"}</DialogTitle>
        <DialogContent dividers>
          <TextField fullWidth margin="dense" label="Nome do grupo" value={fSubject} onChange={(e) => setFSubject(e.target.value)} />
          <TextField fullWidth margin="dense" label="Descrição" multiline minRows={2} value={fDesc} onChange={(e) => setFDesc(e.target.value)} />
          {!editing && (
            <Autocomplete
              multiple options={contacts} getOptionLabel={(o) => `${o.name} (${o.number})`}
              value={fParticipants} onChange={(_, v) => setFParticipants(v)}
              renderInput={(params) => <TextField {...params} margin="dense" label="Participantes (contatos)" placeholder="Buscar contato" />}
            />
          )}
          <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 10 }}>
            <input ref={fFileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => setFFile(e.target.files?.[0] || null)} />
            <Button variant="outlined" onClick={() => fFileRef.current?.click()}>{editing ? "Trocar imagem" : "Imagem do grupo"}</Button>
            {fFile && <Chip label={fFile.name} onDelete={() => setFFile(null)} />}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancelar</Button>
          <Button variant="contained" color="primary" onClick={saveForm} disabled={saving}>{saving ? <CircularProgress size={20} /> : "Salvar"}</Button>
        </DialogActions>
      </Dialog>

      {/* Participantes */}
      <Dialog open={partOpen} onClose={() => setPartOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Participantes — {partGroup?.subject}</DialogTitle>
        <DialogContent dividers>
          <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            <TextField fullWidth label="Adicionar números (vírgula)" value={partNew} onChange={(e) => setPartNew(e.target.value)} />
            <Button variant="outlined" onClick={() => { const ns = partNew.split(",").map(s => s.trim()).filter(Boolean); if (ns.length) partAction(ns, "add"); }}>Adicionar</Button>
          </div>
          <Table size="small">
            <TableBody>
              {partList.map((p) => (
                <TableRow key={p.id}>
                  <TableCell>{String(p.id).split("@")[0]}{p.admin ? " 👑" : ""}</TableCell>
                  <TableCell align="right">
                    {p.admin
                      ? <Button size="small" onClick={() => partAction([String(p.id).split("@")[0]], "demote")}>Rebaixar</Button>
                      : <Button size="small" onClick={() => partAction([String(p.id).split("@")[0]], "promote")}>Promover</Button>}
                    <Button size="small" color="secondary" onClick={() => partAction([String(p.id).split("@")[0]], "remove")}>Remover</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions><Button onClick={() => setPartOpen(false)}>Fechar</Button></DialogActions>
      </Dialog>

      <GroupMessageDialog open={msgOpen} onClose={() => setMsgOpen(false)} whatsappId={whatsappId} groups={groups} />
    </div>
  );
};

export default Groups;
